import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["value", "disabled"]
const _hoisted_5 = ["value", "disabled"]
const _hoisted_6 = ["value", "disabled"]
const _hoisted_7 = { columns: "2" }
const _hoisted_8 = ["value", "disabled"]
const _hoisted_9 = ["value", "disabled"]
const _hoisted_10 = { slot: "footer" }
const _hoisted_11 = ["disabled"]

import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import { EPINIO_TYPES } from '../../types';
import { epinioExceptionToErrorsArray } from '../../utils/errors';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { mapSettingsFromApiResponse, mapSettingsToApiRequest, validateSettings } from '../../utils/settings';
import Banner from '@components/Banner/Banner.vue';
import EpinioAppChartModel from '../../models/appcharts';
import ChartSettings from '../settings/ChartSettings.vue';
import { ConfigSetting } from '../settings/ChartSettings.vue';

import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';


export default /*@__PURE__*/_defineComponent({
  __name: 'ChartsModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');

const initialValues = ref<EpinioAppChartModel | null>(null);

// Form fields (separate from the model to avoid proxy mutation issues)
const chartName = ref('');
const chartShortDescription = ref('');
const chartDescription = ref('');
const helmChartUrl = ref('');
const helmRepoUrl = ref('');
const chartSettings = ref<ConfigSetting[]>([]);

const saving = ref(false);
const errors = ref<string[]>([]);
const hasAssociatedApps = ref<boolean>(false);

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

const isDirty = computed(() => {
  if (!initialValues.value) {
    return chartName.value !== '' ||
      chartShortDescription.value !== '' ||
      chartDescription.value !== '' ||
      helmChartUrl.value !== '' ||
      helmRepoUrl.value !== '' ||
      chartSettings.value.length > 0;
  }

  const initialSettings = mapSettingsFromApiResponse(initialValues.value);

  const isDirty = chartName.value !== (initialValues.value!.meta.name || '') ||
    chartShortDescription.value !== (initialValues.value!.short_description || '') ||
    chartDescription.value !== (initialValues.value!.description || '') ||
    helmChartUrl.value !== (initialValues.value!.helm_chart || '') ||
    helmRepoUrl.value !== (initialValues.value!.helm_repo || '') ||
    !isEqual(sortBy(chartSettings.value, 'name'), sortBy(initialSettings, 'name'));

  return isDirty;
});

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (!chartName.value) return false;
  if (!chartShortDescription.value) return false;
  if (!chartDescription.value) return false;
  if (!helmRepoUrl.value && !helmChartUrl.value) return false;

  const settingsValid = validateSettings(chartSettings.value);
  if (!settingsValid) return false;

  const nameErrors = validateKubernetesName(chartName.value, '', store.getters, undefined, []);
  return nameErrors.length === 0;
});

const canSave = computed(() => {
  const dirty = isDirty.value;
  const valid = validationPassed.value;
  return dirty && valid && !saving.value;
});

function openCreate() {
  errors.value = [];
  modalMode.value = 'create';
  chartName.value = '';
  chartShortDescription.value = '';
  chartDescription.value = '';
  helmChartUrl.value = '';
  helmRepoUrl.value = '';
  chartSettings.value = [];
  showModal.value = true;
}

function openEdit(row: EpinioAppChartModel) {
  errors.value = [];
  modalMode.value = 'edit';
  initialValues.value = row;
  chartName.value = row.name || row.meta?.name || '';
  chartShortDescription.value = row.short_description || '';
  chartDescription.value = row.description || '';
  helmChartUrl.value = row.helm_chart || '';
  helmRepoUrl.value = row.helm_repo || '';
  hasAssociatedApps.value = !!row.bound_apps;
  chartSettings.value = mapSettingsFromApiResponse(row);
  showModal.value = true;
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  chartName.value = '';
  chartShortDescription.value = '';
  chartDescription.value = '';
  helmChartUrl.value = '';
  helmRepoUrl.value = '';
  chartSettings.value = [];
  errors.value = [];
  showDiscardConfirm.value = false;
  showModal.value = false;
  initialValues.value = null;
  hasAssociatedApps.value = false;
}

async function onSubmit() {
  if (!validationPassed.value || !isDirty.value || saving.value) return;

  saving.value = true;
  errors.value = [];

  const { settings, values } = mapSettingsToApiRequest(chartSettings.value);

  try {
    if (isEdit.value && initialValues.value) {
      const chart = initialValues.value;

      chart.description       = chartDescription.value;
      chart.short_description = chartShortDescription.value;
      chart.helm_chart        = helmChartUrl.value;
      chart.helm_repo         = helmRepoUrl.value;
      chart.settings          = settings;
      chart.values            = values;

      await chart.update();
      store.dispatch('growl/success', {
        title:   t('epinio.growl.appCharts.update.success.title'),
        message: t('epinio.growl.appCharts.update.success.message', { name: chartName.value }),
      });
      closeModal();
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP_CHARTS, opt: { force: true } }).catch(() => {});
    } else {
      const chart = await store.dispatch('epinio/create', { type: EPINIO_TYPES.APP_CHARTS });

      chart.metadata          = { name: chartName.value };
      chart.description       = chartDescription.value;
      chart.short_description = chartShortDescription.value;
      chart.helm_chart        = helmChartUrl.value;
      chart.helm_repo         = helmRepoUrl.value;
      chart.settings          = settings;
      chart.values            = values;

      await chart.create();
      store.dispatch('growl/success', {
        title:   t('epinio.growl.appCharts.create.success.title'),
        message: t('epinio.growl.appCharts.create.success.message', { name: chartName.value }),
      });
      closeModal();
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP_CHARTS, opt: { force: true } }).catch(() => {});
    }
  } catch (err: any) {
    errors.value = epinioExceptionToErrorsArray(err);
    store.dispatch('growl/error', {
      title: isEdit.value
        ? t('epinio.growl.appCharts.save.error.updateTitle')
        : t('epinio.growl.appCharts.save.error.createTitle'),
      message: t('epinio.growl.appCharts.save.error.message'),
    });
    console.error('Error saving chart:', err);
  } finally {
    saving.value = false;
  }
}

__expose({ openCreate, openEdit });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: (isView.value || isEdit.value) ? chartName.value|| 'App Chart' : 'App Chart',
    subtitle: (isView.value || isEdit.value) ? '' : 'Create New',
    position: "top",
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        (hasAssociatedApps.value)
          ? (_openBlock(), _createBlock(Banner, {
              key: 0,
              color: "warning",
              label: "This chart is currently associated with one or more applications. Editing it may cause issues for future rebuilds."
            }))
          : _createCommentVNode("", true),
        _cache[11] || (_cache[11] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createElementVNode("trailhand-text-input", {
            value: chartName.value,
            label: "Name",
            placeholder: "A Unique Name",
            required: true,
            disabled: isView.value || isEdit.value,
            onTextInputChange: _cache[0] || (_cache[0] = (e) => { chartName.value = e.detail.value; })
          }, null, 40, _hoisted_4),
          _cache[7] || (_cache[7] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: chartShortDescription.value,
            label: "Short Description",
            placeholder: "A brief description",
            required: true,
            disabled: isView.value,
            onTextInputChange: _cache[1] || (_cache[1] = (e) => { chartShortDescription.value = e.detail.value; })
          }, null, 40, _hoisted_5)
        ]),
        _cache[12] || (_cache[12] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createElementVNode("trailhand-text-area", {
            value: chartDescription.value,
            label: "Description",
            placeholder: "A detailed description",
            disabled: isView.value,
            required: "",
            onTextAreaChange: _cache[2] || (_cache[2] = (e) => { chartDescription.value = e.detail.value; })
          }, null, 40, _hoisted_6)
        ]),
        _cache[13] || (_cache[13] = _createTextVNode()),
        _createElementVNode("div", null, [
          _cache[9] || (_cache[9] = _createElementVNode("label", { style: {"font-size":"11px","color":"var(--th-input-label)"} }, [
            _createTextVNode("Helm URLs - Provide at least one of the following: "),
            _createElementVNode("span", { style: {"color":"var(--th-color-red)"} }, "*")
          ], -1)),
          _cache[10] || (_cache[10] = _createTextVNode()),
          _createElementVNode("trailhand-form-row", _hoisted_7, [
            _createElementVNode("trailhand-text-input", {
              value: helmChartUrl.value,
              label: "Helm Chart URL",
              placeholder: "e.g. https://example.com/charts/mychart-0.1.0.tgz",
              disabled: isView.value,
              onTextInputChange: _cache[3] || (_cache[3] = (e) => { helmChartUrl.value = e.detail.value; })
            }, null, 40, _hoisted_8),
            _cache[8] || (_cache[8] = _createTextVNode()),
            _createElementVNode("trailhand-text-input", {
              value: helmRepoUrl.value,
              label: "Helm Repo URL",
              placeholder: "e.g. https://example.com/charts/index.yaml",
              disabled: isView.value,
              onTextInputChange: _cache[4] || (_cache[4] = (e) => { helmRepoUrl.value = e.detail.value; })
            }, null, 40, _hoisted_9)
          ])
        ]),
        _cache[14] || (_cache[14] = _createTextVNode()),
        _createVNode(ChartSettings, {
          modelValue: chartSettings.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((chartSettings).value = $event)),
          disabled: isView.value,
          "allow-defaults": ""
        }, null, 8, ["modelValue", "disabled"])
      ]),
      _cache[15] || (_cache[15] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
        return (_openBlock(), _createBlock(Banner, {
          key: i,
          color: "error",
          label: err
        }, null, 8, ["label"]))
      }), 128))
    ]),
    _cache[21] || (_cache[21] = _createTextVNode()),
    _createElementVNode("div", _hoisted_10, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[16] || (_cache[16] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              onButtonClick: _cache[6] || (_cache[6] = ($event: any) => (modalMode.value = 'edit'))
            }, "\n          Edit Configuration\n        ", 32)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[17] || (_cache[17] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[18] || (_cache[18] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[19] || (_cache[19] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleModalClose
              }, "\n          Cancel\n        ", 32),
              _cache[20] || (_cache[20] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "primary",
                disabled: !canSave.value,
                onButtonClick: onSubmit
              }, _toDisplayString(saving.value ? (isEdit.value ? 'Saving...' : 'Creating...') : (isEdit.value ? _unref(t)('generic.save') : _unref(t)('generic.create'))), 41, _hoisted_11)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})